import { useCallback, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import api from '../api/client';
import { getKonspektExtensions } from '../konspekt/konspektEditorExtensions';
import { KONSPEKT_DEFAULT_PYTHON } from '../konspekt/konspektPythonBlocksExtension.jsx';

export default function KonspektTiptapEditor({ initialDocJson, onDocJsonChange }) {
  const extensions = useMemo(() => getKonspektExtensions({ editable: true }), []);

  const editor = useEditor({
    extensions,
    content: initialDocJson || { type: 'doc', content: [{ type: 'paragraph' }] },
    onUpdate: ({ editor: ed }) => {
      onDocJsonChange?.(ed.getJSON());
    },
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || 'https://';
    const url = window.prompt('Адрес ссылки', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(async () => {
    if (!editor) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,image/gif';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/materials/konspekty/upload-image/', fd);
        if (data?.url) {
          editor.chain().focus().setImage({ src: data.url }).run();
        }
      } catch (e) {
        window.alert(e.response?.data?.detail || 'Не удалось загрузить изображение');
      }
    };
    input.click();
  }, [editor]);

  const insertPyRun = useCallback(() => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'konspektPyRun',
        attrs: { code: KONSPEKT_DEFAULT_PYTHON, stdin: '' },
      })
      .run();
  }, [editor]);

  const insertCodeStatic = useCallback(() => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'konspektCodeStatic',
        attrs: { code: KONSPEKT_DEFAULT_PYTHON },
      })
      .run();
  }, [editor]);

  if (!editor) {
    return <div className="konspekt-readonly-loading">Редактор…</div>;
  }

  return (
    <div className="konspekt-tiptap-shell konspekt-tiptap-editor-wrap">
      <div className="konspekt-toolbar" role="toolbar">
        <button type="button" className={editor.isActive('bold') ? 'active' : ''} onClick={() => editor.chain().focus().toggleBold().run()}>
          Ж
        </button>
        <button type="button" className={editor.isActive('italic') ? 'active' : ''} onClick={() => editor.chain().focus().toggleItalic().run()}>
          К
        </button>
        <button type="button" className={editor.isActive('underline') ? 'active' : ''} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          Ч
        </button>
        <span className="konspekt-toolbar-sep" />
        <button type="button" className={editor.isActive('code') ? 'active' : ''} onClick={() => editor.chain().focus().toggleCode().run()} title="Код в строке">
          {'</>'}
        </button>
        <span className="konspekt-toolbar-sep" />
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'active' : ''}>
          H1
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'active' : ''}>
          H2
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'active' : ''}>
          H3
        </button>
        <span className="konspekt-toolbar-sep" />
        <label className="konspekt-color-label">
          Цвет
          <input
            type="color"
            aria-label="Цвет текста"
            onInput={(e) => editor.chain().focus().setColor(e.currentTarget.value).run()}
          />
        </label>
        <button type="button" onClick={() => editor.chain().focus().unsetColor().run()}>
          Сброс цвета
        </button>
        <span className="konspekt-toolbar-sep" />
        <button type="button" onClick={setLink}>
          Ссылка
        </button>
        <button type="button" onClick={addImage}>
          Картинка
        </button>
        <button type="button" onClick={insertPyRun} title="Python: запуск и правка">
          Код + запуск
        </button>
        <button type="button" onClick={insertCodeStatic} title="Только пример кода">
          Пример кода
        </button>
      </div>
      <EditorContent editor={editor} className="konspekt-editor-prose" />
    </div>
  );
}
